import { useCallback, useEffect, useReducer, useRef } from "react";
import { useDispatch } from "react-redux";
import { unwrapResult } from "@reduxjs/toolkit";
import { useNavigate, useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import { toast } from "react-toastify";

import { errorNormalizer } from "../../../shared/Helpers/functions";
import { useResetCalendarData } from "../../Calendar/Helpers/hooks";
import {
  ACTIONS,
  behaviorTreatmentReducer,
  initialBehaviorTreatmentState,
} from "./behaviorTreatmentReducer";
import BehaviorTreatmentData from "./behaviorTreatmentData";
import SoapNoteButtons from "../components/soapNoteButtons";

import { DispatchProperties, useSelector } from "../../../redux/store";
import {
  getSoapNote,
  updateBehaviorTreatment,
} from "../../../redux/State/clientSlice/soapNoteSlice";
import {
  setDate,
  setEndTime,
  setStartTime,
} from "../../../redux/State/clientSlice/sessionSlice";
import {
  BehaviorTreatmentProperties,
  UpdateBehaviorTreatmentProperties,
} from "../../../redux/API/ClientAPIHelpers/soapNoteProperties";

const BehaviorTreatment = () => {
  const dispatch = useDispatch<DispatchProperties>();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { resetData } = useResetCalendarData();

  const reportId = params.get("reportId");
  const clientId = params.get("clientId");
  const sessionId = params.get("sessionId");

  const forcedOperation =
    params.get("forcedOperation") === "true" ? true : false;

  const sessionInfo = useSelector((state) => state.session.sessionInfo);

  const [data, setData] = useReducer(
    behaviorTreatmentReducer,
    initialBehaviorTreatmentState
  );
  const currentUpdateRequestRef = useRef<ReturnType<typeof dispatch<any>> | null>(null);
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
      if (currentUpdateRequestRef.current) {
        console.log("Aborting request on unmount");
        currentUpdateRequestRef.current.abort();
      }
    };
  }, []);

  useEffect(() => {
    if (!reportId || !clientId || !sessionId) return;

    dispatch(getSoapNote({ reportId, clientId }))
      .then(unwrapResult)
      .then((response) => {
        const {
          barrierToTreatmentResponses,
          behaviorTreatmentTechniquesResponses,
          promptLevelsResponses,
          reinforcementResponses,
          skillDomainAddressedResponses,
          maladaptiveBehaviorTechniquesResponses,
          clientMoodResponses,
          behaviorTreatmentFuturePlanResponses,
          location,
          date,
          startTime,
          endTime,
          participantResponses,
        } = response as BehaviorTreatmentProperties;
        const barriers = barrierToTreatmentResponses?.map((x) => x.id);
        const techniques = behaviorTreatmentTechniquesResponses?.map(
          (x) => x.id
        );
        const levels = promptLevelsResponses?.map((x) => x.id);
        const reinforcements = reinforcementResponses?.map((x) => x.id);
        const skillDomains = skillDomainAddressedResponses?.map((x) => x.id);
        const maladaptiveBehaviorTechniques =
          maladaptiveBehaviorTechniquesResponses?.map((x) => x.id);
        const clientMoods = clientMoodResponses?.map((x) => x.id);
        const behaviorTreatmentFuturePlans =
          behaviorTreatmentFuturePlanResponses?.map((x) => x.id);
        const participants = participantResponses?.map((x) => x.id);

        dispatch(setDate(dayjs(date).format()));
        dispatch(setStartTime(dayjs(startTime).format()));
        dispatch(setEndTime(dayjs(endTime).format()));

        setData({
          type: ACTIONS.setState,
          payload: response,
        });

        setData({
          type: ACTIONS.setBarrierToTreatmentResponses,
          payload: barriers,
        });

        setData({
          type: ACTIONS.setBehaviorTreatmentTechniquesResponses,
          payload: techniques,
        });

        setData({
          type: ACTIONS.setClientMoodResponses,
          payload: clientMoods,
        });

        setData({
          type: ACTIONS.setFuturePlanResponses,
          payload: behaviorTreatmentFuturePlans,
        });

        setData({
          type: ACTIONS.setPromptLevelsResponses,
          payload: levels,
        });

        setData({
          type: ACTIONS.setReinforcementResponses,
          payload: reinforcements,
        });

        setData({
          type: ACTIONS.setSkillDomainAddressed,
          payload: skillDomains,
        });

        setData({
          type: ACTIONS.setMaladaptiveTechniquesResponses,
          payload: maladaptiveBehaviorTechniques,
        });

        setData({
          type: ACTIONS.setParticipants,
          payload: participants,
        });

        if (!!location) {
          setData({
            type: ACTIONS.setLocationId,
            payload: location.id,
          });
        }
      })
      .catch(errorNormalizer);
  }, [dispatch, clientId, reportId, sessionId]);

  const updateSoapNoteHandler = useCallback(
    (submit: boolean) => {
      if (currentUpdateRequestRef.current) {
        console.log("Aborting previous pending request");
        currentUpdateRequestRef.current.abort();
      }
      if (!reportId || !clientId || !data) return;
      const { date, startTime, endTime } = sessionInfo;
      const allData: UpdateBehaviorTreatmentProperties = {
        ...data,
        date,
        startTime,
        endTime,
        reportId,
        submit,
        forcedOperation,
        clientId,
      };

      const requestPromise = dispatch(updateBehaviorTreatment(allData));
      currentUpdateRequestRef.current = requestPromise;

      requestPromise
        .then(unwrapResult)
        .then(() => {
          if (!submit || !isMounted.current) return;
          resetData();
          toast("Success");
          navigate("/calendar");
        })
        .catch(errorNormalizer)
        .finally(() => {
          if (currentUpdateRequestRef.current === requestPromise) {
            currentUpdateRequestRef.current = null;
          }
        })
    },
    [
      reportId,
      clientId,
      forcedOperation,
      data,
      sessionInfo,
      navigate,
      resetData,
      dispatch,
    ]
  );

  const saveHandler = useCallback(
    () => updateSoapNoteHandler(false),
    [updateSoapNoteHandler]
  );
  const submitHandler = useCallback(
    () => updateSoapNoteHandler(true),
    [updateSoapNoteHandler]
  );

  return (
    <>
      <BehaviorTreatmentData setData={setData} data={data} save={saveHandler} />
      <SoapNoteButtons save={saveHandler} submit={submitHandler} />
    </>
  );
};

export default BehaviorTreatment;
